import { useMemo, useState } from 'react';
import { Flex, Select, Spin } from 'antd';
import type { SelectProps } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

interface Option {
  label: React.ReactNode | string;
  value: string;
}

interface CustomSelectProps extends SelectProps<string | string[] | null | undefined> {
  options: Option[];
  /**
   * When true, skips client-side filtering; `options` are driven by the parent (e.g. async search).
   * Calls `onSearch` from props on each search input (debouncing should be done in the parent).
   */
  remoteSearch?: boolean;
  /**
   * When false, do not allow to click on "+" to select custom options
   * @default true
   */
  customOptions?: boolean;
}

/** Normalize Select value to a list of string tokens (supports mode="multiple"). */
export const selectedTokens = (val: string | string[] | null | undefined): string[] => {
  if (val == null || val === '') {
    return [];
  }
  if (Array.isArray(val)) {
    return val.filter((v): v is string => typeof v === 'string' && v.length > 0);
  }
  return [val];
};

/** Prepends a custom "add this value" row when the query is not already an exact option (shows + in optionRender). */
export const prependSyntheticCustomOptionIfNeeded = (search: string, next: Option[]): Option[] => {
  const trimmed = search.trim();
  if (trimmed === '') {
    return next;
  }
  const hasExactMatch = next.some((opt) => opt.value.toLowerCase() === trimmed.toLowerCase());
  if (hasExactMatch) {
    return next;
  }
  return [{ label: search, value: search }, ...next];
};

/**
 * Merge remote hits with pinned labels so selected tags keep their display text after a new search.
 */
export function mergeOptionsWithPinnedLabels(
  hits: { label: React.ReactNode | string; value: string }[],
  selected: string[],
  labelByValue: Record<string, React.ReactNode | string>
): { label: React.ReactNode | string; value: string }[] {
  const selectedSet = new Set(selected);
  const map = new Map<string, React.ReactNode | string>();
  for (const o of hits) {
    if (selectedSet.has(o.value) && labelByValue[o.value]) {
      map.set(o.value, labelByValue[o.value]);
    } else {
      map.set(o.value, o.label);
    }
  }
  for (const v of selected) {
    if (!map.has(v) && labelByValue[v]) {
      map.set(v, labelByValue[v]);
    }
  }
  return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  options: initialOptions,
  remoteSearch,
  customOptions = true,
  value: valueFromParent,
  defaultValue,
  onChange: onChangeFromParent,
  loading,
  ...props
}) => {
  const [internalValue, setInternalValue] = useState<string | string[] | null | undefined>(
    defaultValue !== undefined ? defaultValue : null
  );

  const isControlled = valueFromParent !== undefined;
  const effectiveValue = isControlled ? valueFromParent : internalValue;

  // Ensure every selected value has a visible option (required for multiple + async options).
  const buildOptionsWithValue = (baseOptions: Option[], val: string | string[] | null | undefined): Option[] => {
    let result = [...baseOptions];
    for (const token of selectedTokens(val)) {
      const exists = result.some((opt) => opt.value.toLowerCase() === token.toLowerCase());
      if (!exists) {
        result = [...result, { label: token, value: token }];
      }
    }
    return result;
  };

  // Seme avec `effectiveValue`, pas `defaultValue` : en mode CONTROLE
  // (`value=...`), defaultValue est undefined, donc l'etat initial ne portait
  // aucune option pour la valeur selectionnee et le premier rendu ouvrait un
  // dropdown VIDE. L'ancien useEffect masquait ce trou parce qu'un effet tourne
  // au montage — la garde en rendu ci-dessous, non.
  const [localOptions, setLocalOptions] = useState<Option[]>(() =>
    buildOptionsWithValue(initialOptions, effectiveValue)
  );
  const [searchValue, setSearchValue] = useState<string>();

  // Parent-driven search: derive options on each render so Ant Design updates the open dropdown.
  const isParentDrivenSearch = remoteSearch || props.filterOption === false;

  const selectOptions = useMemo(() => {
    if (!isParentDrivenSearch) {
      return localOptions;
    }
    let next = buildOptionsWithValue(initialOptions, effectiveValue);
    if (customOptions && searchValue != null && searchValue.length > 0) {
      next = prependSyntheticCustomOptionIfNeeded(searchValue, next);
    }
    return next;
  }, [isParentDrivenSearch, localOptions, initialOptions, effectiveValue, customOptions, searchValue]);

  const handleSearch = (search: string) => {
    setSearchValue(search);

    if (isParentDrivenSearch) {
      props.onSearch?.(search);
      return;
    }

    if (search.length === 0) {
      setLocalOptions(buildOptionsWithValue(initialOptions, effectiveValue));
      return;
    }

    const filtered = initialOptions.filter((option) =>
      option.label?.toString()?.toLowerCase().includes(search.toLowerCase())
    );

    const hasExactMatch = filtered.some(
      (opt) => opt.value.toLowerCase() === search.toLowerCase()
    );

    let finalOptions =
      !customOptions || hasExactMatch || search.trim() === ''
        ? [...filtered]
        : [{ label: search, value: search }, ...filtered];

    finalOptions = buildOptionsWithValue(finalOptions, effectiveValue);

    setLocalOptions(finalOptions);
  };

  // Local filter mode: sync when parent options or selection change.
  // Ajuste pendant le rendu (pattern « ajuster l'etat quand une prop change » de
  // la doc React) : dans un effet, ce recalcul coutait un second rendu.
  // Gardes REFERENTIELLES, une par dependance de l'effet d'origine. Une cle
  // String(...) concatenee ne marche pas ici : ces valeurs sont typees
  // string | string[] | null | undefined, donc String(['a','b']) rend "a,b" et
  // ['a,b'] produit la MEME cle que ['a','b'] — la garde ne tirait pas et la liste
  // d'options ne se reconstruisait plus. null collisionnait aussi avec une option
  // litterale "null".
  const [lastInitialOptions, setLastInitialOptions] = useState(initialOptions);
  const [lastValueFromParent, setLastValueFromParent] = useState(valueFromParent);
  const [lastInternalValue, setLastInternalValue] = useState(internalValue);
  const [lastParentDriven, setLastParentDriven] = useState(isParentDrivenSearch);
  if (
    initialOptions !== lastInitialOptions ||
    valueFromParent !== lastValueFromParent ||
    internalValue !== lastInternalValue ||
    isParentDrivenSearch !== lastParentDriven
  ) {
    setLastInitialOptions(initialOptions);
    setLastValueFromParent(valueFromParent);
    setLastInternalValue(internalValue);
    setLastParentDriven(isParentDrivenSearch);
    if (!isParentDrivenSearch) {
      setLocalOptions(buildOptionsWithValue(initialOptions, effectiveValue));
    }
  }

  return (
    <Select
      {...props}
      style={{ width: '100%', ...props.style }}
      showSearch
      value={effectiveValue}
      onChange={(newValue) => {
        if (!isControlled) {
          setInternalValue(newValue as string | string[] | null | undefined);
        }
        if (!isParentDrivenSearch) {
          setLocalOptions(buildOptionsWithValue(initialOptions, newValue as string | string[] | null | undefined));
        }
        setSearchValue(undefined);
        onChangeFromParent?.(newValue as never);
      }}
      searchValue={searchValue}
      options={selectOptions.map((option: Option) => ({
        value: option.value,
        label: option.label,
      }))}
      optionRender={(option) => {
        return (
          <Flex align="center" gap="8px">
            {option.value === searchValue && !initialOptions.some((opt) => opt.value === option.value) ? (
              <PlusOutlined />
            ) : null}
            {option.label}
          </Flex>
        );
      }}
      onSearch={handleSearch}
      suffixIcon={loading ? <Spin size="small" /> : undefined}
    />
  );
};

export default CustomSelect;
